import { forwardRef, useId } from 'react'

import { Input as AntdInput, InputProps, InputRef } from 'antd'
import styled, { css } from 'styled-components'

import ChevronUpIcon from 'src/Assets/Svg/common/up-arrow.svg?react'
import ChevronDownIcon from 'src/Assets/Svg/common/down-arrow.svg?react'
import { variables } from 'src/Styled/variables'

import { getStepValue } from './helpers'

const commonStyles = css`
  font-size: ${variables.fontSize12};
  opacity: 1;
  top: 7px;
  left: 16px;
  transition: all 0.2s ease;
  margin: 0;
  z-index: 2;
`

const Wrapper = styled.div`
  position: relative;
  width: 100%;

  &:focus-within p {
    ${commonStyles}
  }

  &:has(input[value]:not([value=''])) {
    p {
      ${commonStyles}
    }

    > input,
    > .ant-input-affix-wrapper {
      padding-top: 24px !important;
      padding-bottom: 7px !important;
    }

    input[type='number'] {
      margin-top: -8px !important;
      padding-top: 8px !important;
      padding-bottom: 4px !important;
    }
  }
`

const Label = styled.p<{
  $disabled?: boolean
}>`
  font-family: 'Segoe UI';
  color: ${({ theme, $disabled }) =>
    $disabled ? theme.color.lightGrey : theme.color.slateGrey} !important;

  font-size: ${variables.fontSize14};
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
  opacity: 0;
  transition: all 0.2s ease;
  position: absolute;
  top: 27px;
  left: 18px;
  z-index: 0;
`

const ErrorMessage = styled.div`
  font-family: 'Segoe UI';
  color: ${({ theme }) => theme.color.mediumRed};
  font-size: ${variables.fontSize14};
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
  margin-top: 5px;
`

const ValidationMessage = styled.div`
  font-family: 'Segoe UI';
  color: ${({ theme }) => theme.color.mediumOrange};
  font-size: ${variables.fontSize14};
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
  margin-top: 5px;
`

const StyledInput = styled(AntdInput)<{
  $isError?: boolean
  $isValidationError?: boolean
}>`
  color: ${({ theme }) => theme.color.darkGrey};
  background-color: ${({ theme }) => theme.color.white} !important;
  border-radius: 16px;
  padding: 16px !important;

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0 1000px ${({ theme }) => theme.color.white} inset !important;
    -webkit-text-fill-color: ${({ theme }) => theme.color.darkGrey} !important;
  }

  border: ${({ theme, $isError, $isValidationError }) => {
    if ($isError) {
      return `1px solid ${theme.color.mediumRed}`
    }
    if ($isValidationError) {
      return `1px solid ${theme.color.mediumOrange}`
    }
    return `1px solid ${theme.color.lightGrey}`
  }} !important;
  position: relative;

  .ant-input {
    padding: 0;
    background-color: ${({ theme }) => theme.color.white} !important;
    color: ${({ theme }) => theme.color.darkGrey};
  }

  &:hover {
    background-color: ${({ theme }) => theme.color.hoverWhite} !important;

    input {
      background-color: ${({ theme }) => theme.color.hoverWhite} !important;
    }
  }

  &:focus {
    background-color: ${({ theme }) => theme.color.white} !important;
    padding-top: 24px !important;
    padding-bottom: 7px !important;

    border: ${({ theme, $isError, $isValidationError }) => {
      if ($isError) {
        return `1px solid ${theme.color.mediumRed}`
      }
      if ($isValidationError) {
        return `1px solid ${theme.color.mediumOrange}`
      }
      return `1px solid ${theme.color.brandGreen}`
    }} !important;
  }

  &.ant-input-affix-wrapper-focused {
    background-color: ${({ theme }) => theme.color.white} !important;
    padding-top: 24px !important;
    padding-bottom: 7px !important;

    border: ${({ theme, $isError, $isValidationError }) => {
      if ($isError) {
        return `1px solid ${theme.color.mediumRed}`
      }
      if ($isValidationError) {
        return `1px solid ${theme.color.mediumOrange}`
      }
      return `1px solid ${theme.color.brandGreen}`
    }} !important;

    input {
      background-color: ${({ theme }) => theme.color.white} !important;
    }
  }

  &::placeholder {
    color: ${({ theme }) => theme.color.slateGrey};
    font-family: 'Segoe UI';
    line-height: 150%;
    font-size: ${variables.fontSize14};
    font-weight: ${variables.fontWeight400};
    transition: all 0.2s ease;
  }

  &[type='number']::-webkit-inner-spin-button,
  &[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    appearance: none;
    margin: 0;
    margin-top: -12px;
  }

  .ant-input-suffix {
    margin-top: -22px;
  }

  input {
    background-color: ${({ theme }) => theme.color.white} !important;
    color: ${({ theme }) => theme.color.darkGrey};

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus {
      -webkit-box-shadow: 0 0 0 1000px ${({ theme }) => theme.color.white} inset !important;
      -webkit-text-fill-color: ${({ theme }) =>
        theme.color.darkGrey} !important;
    }

    &[type='number']::-webkit-inner-spin-button,
    &[type='number']::-webkit-outer-spin-button {
      -webkit-appearance: none;
      appearance: none;
      margin: 0;
      margin-top: -12px;
    }

    &::placeholder {
      color: ${({ theme }) => theme.color.slateGrey};
      font-family: 'Segoe UI';
      line-height: 150%;
      font-size: ${variables.fontSize14};
      font-weight: ${variables.fontWeight400};
      transition: all 0.2s ease;
    }

    &:focus-visible {
      box-shadow: none;
      background-color: ${({ theme }) => theme.color.white} !important;
      &::placeholder {
        opacity: 0;
        transition: all 0.2s ease;
      }
    }
  }

  &:focus-visible {
    box-shadow: none;
    &::placeholder {
      opacity: 0;
      transition: all 0.2s ease;
    }
  }

  &.ant-input-affix-wrapper-disabled {
    border: ${({ theme }) => `1px solid ${theme.color.lightGrey}`} !important;
    background-color: ${({ theme }) => theme.color.white};

    &:hover {
      background-color: ${({ theme }) => theme.color.white};
    }
  }

  &.ant-input-affix-wrapper-disabled:hover {
    background-color: ${({ theme }) => theme.color.white} !important;
  }

  &&:active {
    background-color: ${({ theme }) => theme.color.white};
  }

  &&.ant-input-disabled {
    background-color: ${({ theme }) => theme.color.white};
    color: ${({ theme }) => theme.color.lightGrey};

    &::placeholder {
      color: ${({ theme }) => theme.color.lightGrey};
    }
  }
`

const NumberArrows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-self: center;
`

const ArrowButton = styled.button`
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.color.slateGrey};
  line-height: 0;

  svg path {
    fill: currentColor;
  }

  &:hover {
    color: ${({ theme }) => theme.color.darkGrey};
  }

  &:disabled {
    color: ${({ theme }) => theme.color.lightGrey};
    cursor: not-allowed;
  }
`

export type PrimaryInputProps = InputProps & {
  error?: string | null
  validationError?: string
  className?: string
}

export const PrimaryInput = forwardRef<InputRef, PrimaryInputProps>(
  (props, ref) => {
    const { placeholder, error, validationError, className, disabled, type } =
      props

    const isNumber = type === 'number'
    const errorId = useId()

    const handleStep = (direction: 'up' | 'down'): void => {
      const next = getStepValue(direction, props)
      if (next === null) {
        return
      }
      props.onChange?.({
        target: { value: String(next) },
      } as React.ChangeEvent<HTMLInputElement>)
    }

    const numberSuffix = (
      <NumberArrows>
        <ArrowButton
          type='button'
          disabled={disabled}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => handleStep('up')}
        >
          <ChevronUpIcon />
        </ArrowButton>
        <ArrowButton
          type='button'
          disabled={disabled}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => handleStep('down')}
        >
          <ChevronDownIcon />
        </ArrowButton>
      </NumberArrows>
    )

    const errorDescribedBy = error ? { 'aria-describedby': errorId } : {}

    return (
      <Wrapper>
        <Label $disabled={disabled}>{placeholder}</Label>
        <StyledInput
          {...props}
          {...errorDescribedBy}
          suffix={isNumber ? numberSuffix : props.suffix}
          $isError={Boolean(error)}
          $isValidationError={Boolean(validationError)}
          ref={ref}
          className={className}
        />
        {error && (
          <ErrorMessage id={errorId} role='alert'>
            {error}
          </ErrorMessage>
        )}
        {validationError && (
          <ValidationMessage>{validationError}</ValidationMessage>
        )}
      </Wrapper>
    )
  },
)

PrimaryInput.displayName = 'PrimaryInput'
